import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { message } from 'antd';

interface DashboardProduct {
  id: string;
  is_active: boolean;
  created_at: string;
  next_billing_date?: string | null;
  product?: {
    name: string;
    description: string | null;
    monthly_price?: number | null;
  } | null;
}

interface ActivityLog {
  id: string;
  action_type: string;
  description: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  client_product_id?: string | null;
}

interface PaymentSummary {
  totalMonthly: number;
  nextDueDate: string | null;
  defaultMethod?: string | null;
  upcomingCharge?: number | null;
}

interface DashboardData {
  products: DashboardProduct[];
  activities: ActivityLog[];
  stats: {
    totalWorkflows: number;
    activeWorkflows: number;
    executionsThisMonth: number;
    successRate: number;
    nextPayment: {
      amount: number | null;
      date: string | null;
    };
  };
  paymentSummary: PaymentSummary;
}

const emptyDashboard: DashboardData = {
  products: [],
  activities: [],
  stats: {
    totalWorkflows: 0,
    activeWorkflows: 0,
    executionsThisMonth: 0,
    successRate: 100,
    nextPayment: {
      amount: null,
      date: null,
    },
  },
  paymentSummary: {
    totalMonthly: 0,
    nextDueDate: null,
  },
};

const handleTableError = (error: any, fallback: any) => {
  if (error && error.message && error.message.includes('does not exist')) {
    logger.warn('Optional table missing', { message: error.message });
    return fallback;
  }

  if (error) {
    throw error;
  }

  return fallback;
};

export const useClientDashboard = (clientId: string | null) => {
  return useQuery<DashboardData>({
    queryKey: ['client-dashboard', clientId],
    enabled: !!clientId,
    retry: 1,
    staleTime: 30000,
    queryFn: async () => {
      if (!clientId) return emptyDashboard;

      // Fetch contracted products with active subscriptions only (free or paid)
      const { data: products, error: productsError } = await supabase
        .from('client_products_with_subscription')
        .select('id,is_active,created_at,next_billing_date,payment_status,subscription_status,product:products!inner(name,description,monthly_price)')
        .eq('client_id', clientId)
        .not('subscription_status', 'is', null)
        .order('created_at', { ascending: false });

      const safeProducts = (productsError
        ? handleTableError(productsError, [])
        : products ?? []) as DashboardProduct[];

      // Fetch recent activities
      const startOfMonth = dayjs().startOf('month').toISOString();
      const { data: activities, error: activitiesError } = await supabase
        .from('activity_log')
        .select('id,action_type,description,metadata,client_product_id,created_at')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false })
        .limit(10);

      const safeActivities = activitiesError
        ? handleTableError(activitiesError, [])
        : (activities as ActivityLog[] | null) ?? [];

      const executionsThisMonth = safeActivities.filter((item: ActivityLog) =>
        dayjs(item.created_at).isAfter(startOfMonth)
      ).length;

      const activeWorkflows = safeProducts.filter((p: DashboardProduct) => p.is_active).length;
      const totalWorkflows = safeProducts.length;

      // Calculate total monthly amount and next payment
      const totalMonthly = safeProducts.reduce((sum: number, product: DashboardProduct) => {
        const price = product.product?.monthly_price ?? 0;
        return sum + Number(price);
      }, 0);

      const nextBillingDate = safeProducts
        .map((item: DashboardProduct) => item.next_billing_date)
        .filter((date): date is string => Boolean(date))
        .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())[0] || null;

      // Fetch payment history summary (optional)
      let defaultMethod: string | null = null;
      let upcomingCharge: number | null = null;
      const { data: payments, error: paymentsError } = await supabase
        .from('payment_history')
        .select('amount,status,due_date')
        .eq('user_id', clientId)
        .order('due_date', { ascending: true })
        .limit(5);

      if (!paymentsError && payments) {
        const nextPayment = payments.find((p) => p.status === 'pending');
        if (nextPayment) {
          upcomingCharge = Number(nextPayment.amount);
        }
      } else if (paymentsError) {
        handleTableError(paymentsError, []);
      }

      // Payment methods (optional)
      const { data: paymentMethods, error: methodsError } = await supabase
        .from('payment_methods')
        .select('card_brand,last_4_digits,is_default')
        .eq('user_id', clientId)
        .order('is_default', { ascending: false })
        .limit(1);

      if (!methodsError && paymentMethods && paymentMethods.length > 0) {
        const method = paymentMethods[0];
        defaultMethod = method.card_brand
          ? `${method.card_brand.toUpperCase()} •••• ${method.last_4_digits}`
          : 'Forma de pagamento cadastrada';
      } else if (methodsError) {
        handleTableError(methodsError, []);
      }

      return {
        products: safeProducts,
        activities: safeActivities,
        stats: {
          totalWorkflows,
          activeWorkflows,
          executionsThisMonth,
          successRate: totalWorkflows === 0 ? 100 : Math.min(100, 85 + activeWorkflows * 3),
          nextPayment: {
            amount: totalMonthly || upcomingCharge,
            date: nextBillingDate,
          },
        },
        paymentSummary: {
          totalMonthly,
          nextDueDate: nextBillingDate,
          defaultMethod,
          upcomingCharge,
        },
      } satisfies DashboardData;
    },
  });
};

export interface SupportTicketInput {
  title: string;
  description: string;
  category: 'technical' | 'financial' | 'general' | 'setup';
  priority: 'low' | 'medium' | 'high';
  client_product_id?: string | null;
  marketplace_product_id?: string | null;
  workflow_name?: string | null;
}

export const useSupportTickets = (clientId: string | null) => {
  return useQuery({
    queryKey: ['support-tickets', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('support_tickets')
        .select('id,title,category,priority,status,created_at,client_product_id')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        handleTableError(error, []);
        return [];
      }

      return data ?? [];
    },
  });
};

export const useCreateSupportTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ input, clientId }: { input: SupportTicketInput; clientId: string }) => {
      const payload = {
        user_id: clientId,
        client_product_id: input.client_product_id ?? null,
        marketplace_product_id: input.marketplace_product_id ?? null,
        title: input.title,
        description: input.description,
        category: input.category,
        priority: input.priority,
      };

      const { data, error } = await supabase
        .from('support_tickets')
        .insert(payload)
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      const webhookUrl = import.meta.env.VITE_SUPPORT_WEBHOOK_URL;
      if (webhookUrl) {
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            ticket_id: data.id,
            created_at: new Date().toISOString(),
          }),
        }).catch((err) => logger.warn('Failed to trigger support webhook', err));
      }

      message.success('Chamado aberto com sucesso!');
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets', variables.clientId] });
    },
  });
};

export const usePaymentHistory = (clientId: string | null) => {
  return useQuery({
    queryKey: ['payment-history', clientId],
    enabled: !!clientId,
    retry: 1,
    staleTime: 30000,
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('payment_history')
        .select('id,amount,status,invoice_url,due_date,created_at')
        .eq('user_id', clientId)
        .order('due_date', { ascending: false });

      if (error) {
        handleTableError(error, []);
        return [];
      }

      return data ?? [];
    },
  });
};

export const useUserPreferences = (userId: string | null) => {
  return useQuery({
    queryKey: ['user-preferences', userId],
    enabled: !!userId,
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        handleTableError(error, null);
        return null;
      }

      return data;
    },
  });
};

export const useUpdateUserPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, values }: { userId: string; values: Record<string, any> }) => {
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: userId, ...values })
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      message.success('Preferências atualizadas com sucesso');
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', variables.userId] });
    },
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, values }: { userId: string; values: Record<string, any> }) => {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(values)
        .eq('id', userId)
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      message.success('Perfil atualizado com sucesso');
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-profile', variables.userId] });
    },
  });
};
